import React, { useEffect, useRef, useState } from 'react';
import { BaseProvider, useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { HeadingSmall, LabelSmall } from 'baseui/typography';
import { lightTheme, darkTheme } from './theme';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { LANGUAGES } from './i18n';
import { useTranslation } from 'react-i18next';
import About from './sections/About';
import Experience from './sections/Experience';
import Contact from './sections/Contact';

type Section = 'about' | 'experience' | 'contact';

const APP_BAR_HEIGHT = '56px';
const SIDEBAR_WIDTH = '220px';

function getActiveFromHash(): Section {
  const hash = window.location.hash.slice(1);
  if (hash === 'experience' || hash === 'contact') return hash;
  return 'about';
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  const [css, theme] = useStyletron();
  const barBase = {
    display: 'block' as const,
    width: '20px',
    height: '2px',
    backgroundColor: theme.colors.contentPrimary,
    borderRadius: '2px',
    transition: 'transform 0.25s, opacity 0.25s',
  };
  return (
    <span className={css({ display: 'flex', flexDirection: 'column', gap: '5px' })}>
      <span className={css({ ...barBase, ...(open ? { transform: 'translateY(7px) rotate(45deg)' } : {}) })} />
      <span className={css({ ...barBase, ...(open ? { opacity: '0' } : {}) })} />
      <span className={css({ ...barBase, ...(open ? { transform: 'translateY(-7px) rotate(-45deg)' } : {}) })} />
    </span>
  );
}

function SidebarNavItem({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  const [css, theme] = useStyletron();
  return (
    <button
      onClick={onClick}
      className={css({
        display: 'block',
        width: '100%',
        background: active ? theme.colors.backgroundSecondary : 'none',
        border: 'none',
        borderLeft: active ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
        cursor: 'pointer',
        padding: `${theme.sizing.scale500} ${theme.sizing.scale700}`,
        textAlign: 'left',
        color: active ? theme.colors.primary : theme.colors.contentPrimary,
        fontFamily: theme.typography.LabelMedium.fontFamily,
        fontSize: theme.typography.LabelMedium.fontSize,
        fontWeight: active ? '600' : '400',
        transition: 'background 0.15s, color 0.15s, border-color 0.15s',
        ':hover': {
          background: theme.colors.backgroundSecondary,
          color: theme.colors.primary,
        },
      })}
    >
      {label}
    </button>
  );
}


function AppInner({ isDark, onToggleDark }: { isDark: boolean; onToggleDark: () => void }) {
  const [css, theme] = useStyletron();
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<Section>(getActiveFromHash);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  const glassBackground = isDark ? 'rgba(61, 92, 58, 0.82)' : 'rgba(197, 191, 168, 0.82)';

  // Inject themed SVG cursors
  useEffect(() => {
    const color = isDark ? '#A08860' : '#9DAEC0';
    const arrow = encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='14' height='18' viewBox='0 0 14 18'>` +
      `<path d='M2 2L2 15L5.5 11.5L8 18L10 17L7.5 10.5L12 10.5Z' fill='${color}' stroke='white' stroke-width='1.5' stroke-linejoin='round' stroke-linecap='round'/>` +
      `</svg>`
    );
    const pointer = encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='14' height='18' viewBox='0 0 14 18'>` +
      `<path d='M2 2L2 15L5.5 11.5L8 18L10 17L7.5 10.5L12 10.5Z' fill='${color}' stroke='white' stroke-width='1.5' stroke-linejoin='round' stroke-linecap='round'/>` +
      `</svg>`
    );
    const arrowCursor = `url("data:image/svg+xml,${arrow}") 2 2, auto`;
    const pointerCursor = `url("data:image/svg+xml,${pointer}") 2 2, pointer`;
    let style = document.getElementById('cursor-style') as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = 'cursor-style';
      document.head.appendChild(style);
    }
    style.textContent = [
      `* { cursor: ${arrowCursor} !important; }`,
      `a, button, select, [role="button"], [tabindex]:not([tabindex="-1"]) { cursor: ${pointerCursor} !important; }`,
    ].join('\n');
  }, [isDark]);

  // Sync body background with theme
  useEffect(() => {
    document.body.style.backgroundColor = theme.colors.backgroundPrimary;
    document.body.style.backgroundImage = 'none';
    document.body.style.minHeight = '100vh';
    document.documentElement.style.setProperty('--caret-color', isDark ? '#A08860' : '#9DAEC0');
  }, [theme.colors.backgroundPrimary, isDark]);

  // Hash-based routing
  useEffect(() => {
    const handleHashChange = () => setActive(getActiveFromHash());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Focus trap + Escape key for sidebar
  useEffect(() => {
    if (!sidebarOpen) return;
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const focusable = Array.from(
      sidebar.querySelectorAll<HTMLElement>('button, [href], input, select, [tabindex]:not([tabindex="-1"])')
    );
    focusable[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
        hamburgerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  const handleNavClick = (key: Section) => {
    window.location.hash = key;
    setSidebarOpen(false);
    hamburgerRef.current?.focus();
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    hamburgerRef.current?.focus();
  };

  const iconButtonStyle = css({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.sizing.scale300,
    borderRadius: theme.borders.radius200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.contentPrimary,
    ':hover': { backgroundColor: theme.colors.backgroundSecondary },
  });

  const NAV_ITEMS: { key: Section; label: string }[] = [
    { key: 'about', label: t('nav.about') },
    { key: 'experience', label: t('nav.experience') },
    { key: 'contact', label: t('nav.contact') },
  ];

  return (
    <ThemeContextProvider isDark={isDark}>
      <Block
        minHeight="100vh"
        display="flex"
        flexDirection="column"
        overrides={{
          Block: {
            style: { overflowX: 'hidden', width: '100%', background: 'transparent' },
          },
        }}
      >
        {/* Animated gradient overlay */}
        <div aria-hidden="true" className="portfolio-gradient" />

        {/* App bar */}
        <Block
          as="header"
          display="flex"
          alignItems="center"
          paddingLeft={theme.sizing.scale600}
          paddingRight={theme.sizing.scale600}
          overrides={{
            Block: {
              style: {
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                height: APP_BAR_HEIGHT,
                backgroundColor: glassBackground,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                zIndex: 200,
              },
            },
          }}
        >
          <button
            ref={hamburgerRef}
            aria-label={t('aria.toggleNav')}
            aria-expanded={sidebarOpen}
            aria-controls="sidebar-nav"
            onClick={() => setSidebarOpen((o) => !o)}
            className={css({
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: theme.sizing.scale300,
              marginRight: theme.sizing.scale600,
              borderRadius: theme.borders.radius200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ':hover': { backgroundColor: theme.colors.backgroundSecondary },
            })}
          >
            <HamburgerIcon open={sidebarOpen} />
          </button>

<Block flex="1" />

          {/* Language selector */}
          <select
            aria-label={t('aria.selectLanguage')}
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className={css({
              appearance: 'none',
              WebkitAppearance: 'none',
              background: `no-repeat right ${theme.sizing.scale300} center / 12px 12px url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
              border: `1px solid ${theme.colors.borderOpaque}`,
              borderRadius: theme.borders.radius200,
              color: theme.colors.contentPrimary,
              fontFamily: theme.typography.LabelSmall.fontFamily,
              fontSize: theme.typography.LabelSmall.fontSize,
              padding: `${theme.sizing.scale100} ${theme.sizing.scale700} ${theme.sizing.scale100} ${theme.sizing.scale300}`,
              cursor: 'pointer',
              marginRight: theme.sizing.scale300,
              outline: 'none',
              ':focus-visible': { outline: `2px solid ${theme.colors.primary}` },
            })}
          >
            {LANGUAGES.map(({ code, label }) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>

          {/* Dark mode toggle */}
          <button
            aria-label={isDark ? t('aria.switchToLight') : t('aria.switchToDark')}
            onClick={onToggleDark}
            className={iconButtonStyle}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
        </Block>

        {/* Sidebar overlay */}
        {sidebarOpen && (
          <div
            aria-hidden="true"
            onClick={closeSidebar}
            className={css({
              position: 'fixed',
              top: APP_BAR_HEIGHT,
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0,0,0,0.35)',
              zIndex: 300,
            })}
          />
        )}

        {/* Sidebar */}
        <Block
          id="sidebar-nav"
          ref={sidebarRef as React.Ref<HTMLElement>}
          as="nav"
          aria-label={t('aria.mainNav')}
          aria-hidden={!sidebarOpen}
          overrides={{
            Block: {
              style: {
                position: 'fixed',
                top: APP_BAR_HEIGHT,
                left: '0',
                bottom: '0',
                width: SIDEBAR_WIDTH,
                backgroundColor: glassBackground,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                zIndex: 400,
                transform: sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_WIDTH})`,
                transition: 'transform 0.25s ease',
                overflowY: 'auto',
              },
            },
          }}
        >
          <Block paddingBottom={theme.sizing.scale600}>
            {NAV_ITEMS.map(({ key, label }) => (
              <SidebarNavItem
                key={key}
                label={label}
                active={active === key}
                onClick={() => handleNavClick(key)}
              />
            ))}
          </Block>
        </Block>

        {/* Main content */}
        <Block
          as="main"
          maxWidth="900px"
          margin="0 auto"
          paddingLeft={theme.sizing.scale600}
          paddingRight={theme.sizing.scale600}
          flex="1"
          overrides={{
            Block: {
              style: {
                paddingTop: `calc(${APP_BAR_HEIGHT} + ${active === 'about' ? '24px' : '0px'})`,
                position: 'relative',
                zIndex: 1,
              },
            },
          }}
        >
          {active === 'about' && <About onContactClick={() => handleNavClick('contact')} onExperienceClick={() => handleNavClick('experience')} />}
          {active === 'experience' && <Experience onContactClick={() => handleNavClick('contact')} />}
          {active === 'contact' && <Contact />}
        </Block>

        {/* Footer */}
        <Block as="footer" paddingTop={theme.sizing.scale600} paddingBottom={theme.sizing.scale600}>
          <LabelSmall color="contentTertiary" overrides={{ Block: { style: { textAlign: 'center', display: 'block' } } }}>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </LabelSmall>
        </Block>
      </Block>
    </ThemeContextProvider>
  );
}

export default function App() {
  const [isDark, setIsDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  return (
    <BaseProvider theme={isDark ? darkTheme : lightTheme}>
      <AppInner isDark={isDark} onToggleDark={() => setIsDark((d) => !d)} />
    </BaseProvider>
  );
}
